import { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import Button from 'components/common/button'
import { SearchIcon, UploadIcon } from 'components/icons'
import Modal from 'components/modal'
import { useQuery } from 'hooks'
import { compareFileExtension } from 'utils/helpers'
import {
  BARCODE_COUNT_KEY,
  BARCODE_ERROR_MESSAGES,
  BARCODE_QUERY_KEY,
  BARCODE_TOKEN_KEY,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  getBarcodeErrorCode,
  normalizeTypedBarcodes,
} from './constants'

type BarcodeSearchResult = { token: string; count: number }

type Props = {
  /**
   * Uploads the barcode CSV and resolves the token + count. Must be a single
   * `FormData` POST with the field named `file` (see the module's api layer).
   */
  uploadFile: (file: File) => Promise<{ data: BarcodeSearchResult }>
}

/**
 * "Advance search by barcode" control. Two ways to scope the list to an exact
 * set of barcodes, both living in the URL so the list refetches automatically:
 *  - Typed / pasted (small lists) → `barcode` (comma-separated, exact `$in`).
 *  - File upload (large lists) → `barcodeToken` (+ `barcodeCount` for display).
 * The two modes are mutually exclusive; starting one clears the other.
 */
const BarcodeSearch = ({ uploadFile }: Props) => {
  const { getQuery, setMultipleQueries } = useQuery()

  const token: string | null = getQuery(BARCODE_TOKEN_KEY)
  const uploadCount = getQuery(BARCODE_COUNT_KEY)
  // A single all-digit barcode comes back JSON-parsed as a number, so coerce.
  const typedValueRaw = getQuery(BARCODE_QUERY_KEY)
  const typedValue = typedValueRaw != null ? String(typedValueRaw) : ''

  const [isOpen, setIsOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const resetFileInput = () => {
    setFile(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const closeModal = () => {
    if (uploading) return
    setIsOpen(false)
    setTyped('')
    resetFileInput()
  }

  const handleFileChange = () => {
    const selected = inputRef.current?.files?.[0]
    if (!selected) return

    // Client-side pre-checks for snappy feedback (backend validates too).
    if (!compareFileExtension(selected, 'csv')) {
      toast.error('Invalid file format. Only CSV files are allowed.', {
        duration: 4000,
      })
      return resetFileInput()
    }

    if (selected.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File must be ${MAX_FILE_SIZE_MB} MB or smaller.`, {
        duration: 4000,
      })
      return resetFileInput()
    }

    setFile(selected)
  }

  // Typed / pasted comma-separated barcodes → exact `barcode` query param.
  const handleTypedSearch = () => {
    const list = normalizeTypedBarcodes(typed)
    if (!list.length) {
      return toast.error('Please enter at least one barcode.', {
        duration: 3000,
      })
    }

    // Set the typed barcodes and clear any active file-upload search — one
    // navigation so nothing clobbers (see setMultipleQueries).
    setMultipleQueries({
      [BARCODE_QUERY_KEY]: list.join(','),
      [BARCODE_TOKEN_KEY]: undefined,
      [BARCODE_COUNT_KEY]: undefined,
      page: 1,
    })

    toast.success(`Searching ${list.length.toLocaleString()} barcodes`, {
      duration: 3000,
    })

    setIsOpen(false)
    setTyped('')
    resetFileInput()
  }

  // Uploaded file → token via the module's api, stored as `barcodeToken`.
  const handleUploadSearch = async () => {
    if (!file) {
      return toast.error('Please choose a file.', { duration: 3000 })
    }

    setUploading(true)
    const loadingToast = toast.loading('Uploading barcodes...', {
      duration: Infinity,
    })

    try {
      const { data } = await uploadFile(file)

      // Store the token + count and clear any active typed search — one
      // navigation so nothing clobbers (see setMultipleQueries).
      setMultipleQueries({
        [BARCODE_TOKEN_KEY]: data.token,
        [BARCODE_COUNT_KEY]: data.count,
        [BARCODE_QUERY_KEY]: undefined,
        page: 1,
      })

      toast.success(
        `Searching ${Number(data.count).toLocaleString()} barcodes`,
        { id: loadingToast, duration: 3000 },
      )

      setIsOpen(false)
      setTyped('')
      resetFileInput()
    } catch (error) {
      const code = getBarcodeErrorCode(error)
      toast.error(
        (code && BARCODE_ERROR_MESSAGES[code]) ||
          'Failed to search by barcode. Please try again.',
        { id: loadingToast, duration: 4000 },
      )
    } finally {
      setUploading(false)
    }
  }

  const clearBarcodeSearch = () => {
    // Drop every barcode-search key and reset to page 1 in ONE navigation.
    setMultipleQueries({
      [BARCODE_QUERY_KEY]: undefined,
      [BARCODE_TOKEN_KEY]: undefined,
      [BARCODE_COUNT_KEY]: undefined,
      page: 1,
    })
  }

  // Generate the single-column template client-side (header only).
  const downloadTemplate = () => {
    const blob = new Blob(['barcode\n'], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'barcode-search-template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  // Active search → show a chip with the barcode count + clear action.
  const isActive = !!token || !!typedValue
  if (isActive) {
    const chipCount = token
      ? Number(uploadCount || 0)
      : normalizeTypedBarcodes(typedValue).length

    return (
      <div className="mb-3 flex items-center gap-3 rounded-lg border border-button-primary bg-button-secondary px-4 py-2.5">
        <span className="inline-flex h-4 w-4 shrink-0 text-button-primary">
          <SearchIcon />
        </span>
        <span className="text-sm font-semibold text-sidebar-primary">
          Barcode search active · {chipCount.toLocaleString()} barcodes
        </span>
        <Button
          type="button"
          buttonText="Clear"
          variant="custom"
          onClick={clearBarcodeSearch}
          className="ml-auto rounded-lg px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50"
        />
      </div>
    )
  }

  return (
    <div className="mb-3">
      <Button
        type="button"
        buttonText="Advance search by barcode"
        variant="custom"
        icon={<SearchIcon />}
        iconClassName="!h-5 !w-5 text-dataTableIcon"
        className="rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-semibold text-sidebar-primary hover:bg-gray-100"
        onClick={() => setIsOpen(true)}
      />

      <Modal
        isOpen={isOpen}
        closeModal={closeModal}
        title="Advance search by barcode"
        width={520}
      >
        <>
          <p className="mb-4 text-sm text-gray-500">
            Scope the list to one or many exact barcodes. Type or paste them
            below, or upload a CSV for large lists.
          </p>

          {/* Type / paste barcodes */}
          <label className="mb-1.5 block text-sm font-semibold text-sidebar-primary">
            Type or paste barcodes
          </label>
          <textarea
            className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-button-primary focus:outline-none"
            rows={3}
            placeholder="e.g. 6060601, 5050501, 0022334455"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={uploading}
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-xs text-gray-400">
              Separate multiple barcodes with commas.
            </p>
            <Button
              type="button"
              buttonText="Search barcodes"
              onClick={handleTypedSearch}
              disabled={!typed.trim() || uploading}
            />
          </div>

          {/* Divider */}
          <div className="my-5 flex items-center gap-3 text-xs font-medium uppercase text-gray-400">
            <span className="h-px flex-1 bg-gray-200" />
            or upload a file
            <span className="h-px flex-1 bg-gray-200" />
          </div>

          {/* Upload a file */}
          <input
            className="hidden"
            type="file"
            accept=".csv"
            ref={inputRef}
            onChange={handleFileChange}
          />

          <div className="flex items-center gap-3">
            <Button
              type="button"
              buttonText={file ? 'Change file' : 'Choose file'}
              variant="outline"
              icon={<UploadIcon />}
              iconClassName="!h-4 !w-4"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            />
            <span className="truncate text-sm text-gray-600">
              {file ? file.name : 'No file selected'}
            </span>
          </div>

          <p className="mt-3 text-xs text-gray-400">
            Only .csv files, up to {MAX_FILE_SIZE_MB} MB.
          </p>

          <div className="mt-6 flex items-center justify-between">
            <Button
              type="button"
              buttonText="Download template"
              variant="outline"
              onClick={downloadTemplate}
              disabled={uploading}
            />

            <div className="flex items-center gap-3">
              <Button
                type="button"
                buttonText="Cancel"
                variant="outline"
                onClick={closeModal}
                disabled={uploading}
              />
              <Button
                type="button"
                buttonText="Upload & search"
                onClick={handleUploadSearch}
                loading={uploading}
                disabled={!file || uploading}
              />
            </div>
          </div>
        </>
      </Modal>
    </div>
  )
}

export default BarcodeSearch
